import React, { useCallback, useState } from 'react';
import { ImageDimensions, PhotoMarker, Unit, Position } from '../types/types';
import { PDFCanvasViewport } from '@/features/pdf-editor/components/viewer/PDFCanvasViewport';
import { PDFViewerState } from '@/features/pdf-editor/types';

interface PDFLayoutViewerProps {
  pdfUrl: string;
  pageNumber?: number;
  cropRegion?: [number, number, number, number] | null;
  onDimensionsLoad: (dimensions: ImageDimensions) => void;
  onFitComplete: (fitScale: number, fitPosition: Position) => void;
  scale: number;
  position: Position;
  onScaleChange: (scale: number) => void;
  onPositionChange: (position: Position) => void;

  // Interaction mode
  mode: 'view' | 'measure' | 'photoIndex' | 'areaTags';
  photoIndexMode: boolean;
  addPhotoMarker: (point: Position) => void;
  addMeasurementPoint: (point: Position) => void;
  setSelectedMarkers: (markers: Set<number>) => void;

  // Marker overlay props
  units: Unit[];
  photoMarkers: PhotoMarker[];
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  selectedUnit: Unit | null;
  currentPolygon: number[][];
  selectedMarkers: Set<number>;
  markersInAreas: Set<PhotoMarker>;
  areaPhotoMap: Map<number, number[]>;
  showCoordinates: boolean;
  handleUnitClick: (unit: Unit, event: React.MouseEvent) => void;
  handleMarkerClick: (marker: PhotoMarker, event: React.MouseEvent) => void;
  deletePhotoMarker: (markerId: number) => void;
  handleMarkerRightClick?: (event: React.MouseEvent) => void;
  handleMarkerDragStart?: (markerId: number, event: React.MouseEvent) => void;
  handleSvgClick: (e: React.MouseEvent) => void;
}

/**
 * PDFLayoutViewer — renders a PDF drawing using the exact same PDFCanvasViewport
 * as the PDF editor (pixel-space CSS transforms, auto-fit, drag/zoom/pan),
 * with an SVG marker overlay aligned to the same transform.
 */
export const PDFLayoutViewer: React.FC<PDFLayoutViewerProps> = ({
  pdfUrl,
  pageNumber = 1,
  cropRegion = null,
  onDimensionsLoad,
  onFitComplete,
  scale,
  position,
  onScaleChange,
  onPositionChange,
  mode,
  photoIndexMode,
  addPhotoMarker,
  addMeasurementPoint,
  setSelectedMarkers,
  units: _units,
  photoMarkers,
  svgRef,
  groupRef,
  selectedUnit,
  currentPolygon: _currentPolygon,
  selectedMarkers,
  markersInAreas,
  areaPhotoMap,
  showCoordinates: _showCoordinates,
  handleUnitClick: _handleUnitClick,
  handleMarkerClick,
  deletePhotoMarker,
  handleMarkerRightClick,
  handleMarkerDragStart,
  handleSvgClick: _handleSvgClick,
}) => {
  const [pdfDimensions, setPdfDimensions] = useState<ImageDimensions | null>(null);

  const handleDimensions = useCallback((dims: ImageDimensions) => {
    setPdfDimensions(dims);
    onDimensionsLoad(dims);
  }, [onDimensionsLoad]);

  const handleLoadSuccess = useCallback(() => {}, []);

  /**
   * Convert a client-space point (from PDFCanvasViewport's onBackgroundClick)
   * into PDF content coordinates using the SVG group's current transform matrix.
   */
  const clientToPdfCoords = useCallback((clientX: number, clientY: number): Position | null => {
    if (!svgRef.current || !groupRef.current) return null;
    const svgEl = svgRef.current;
    const groupEl = groupRef.current;
    const svgRect = svgEl.getBoundingClientRect();
    const ctm = groupEl.getCTM();
    if (!ctm) return null;
    const pt = svgEl.createSVGPoint();
    // getCTM() gives the transform from SVG viewport space → screen space.
    // createSVGPoint coords must be in SVG viewport space, so subtract the rect.
    pt.x = clientX - svgRect.left;
    pt.y = clientY - svgRect.top;
    const transformed = pt.matrixTransform(ctm.inverse());
    return { x: Math.round(transformed.x), y: Math.round(transformed.y) };
  }, [svgRef, groupRef]);

  /**
   * Called by PDFCanvasViewport when the user clicks the background (no drag).
   * Routes to addPhotoMarker or addMeasurementPoint depending on mode.
   */
  const handleBackgroundClick = useCallback((clientX: number, clientY: number) => {
    if (mode === 'measure') {
      const point = clientToPdfCoords(clientX, clientY);
      if (point) addMeasurementPoint(point);
    } else if (photoIndexMode) {
      const point = clientToPdfCoords(clientX, clientY);
      if (point) addPhotoMarker(point);
    } else {
      // In view mode, clicking background clears selection
      setSelectedMarkers(new Set());
    }
  }, [mode, photoIndexMode, clientToPdfCoords, addPhotoMarker, addMeasurementPoint, setSelectedMarkers]);

  const viewerState: PDFViewerState = {
    currentPage: pageNumber,
    numPages: 1,
    rotation: 0,
    scale,
    position,
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Base layer: PDF rendering with built-in drag/zoom/pan */}
      <PDFCanvasViewport
        fileUrl={pdfUrl}
        viewerState={viewerState}
        onLoadSuccess={handleLoadSuccess}
        onScaleChange={onScaleChange}
        onPositionChange={onPositionChange}
        onFitComplete={onFitComplete}
        onDimensionsLoad={handleDimensions}
        onBackgroundClick={handleBackgroundClick}
        loadingMessage="Loading Layout..."
        cropRegion={cropRegion}
      />

      {/* Overlay layer: SVG markers aligned via the same pixel-space transform */}
      {pdfDimensions && (
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{
            position: 'absolute',
            inset: 0,
            // Pointer events are 'none' at SVG root so the canvas can receive
            // drag events. Individual marker <g> elements re-enable them.
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          <g
            ref={groupRef}
            transform={`translate(${position.x}, ${position.y}) scale(${scale})`}
          >
            {photoMarkers.map((marker) => {
              const isSelected = selectedMarkers.has(marker.id);
              const isInArea = markersInAreas.has(marker);
              const isVisible =
                !isInArea ||
                (selectedUnit &&
                  areaPhotoMap
                    .get(selectedUnit.id)
                    ?.some((photoId) => marker.photos.some((p) => p.id === photoId)));

              if (!isVisible && !photoIndexMode) return null;

              return (
                <g
                  key={marker.id}
                  className={`photo-marker ${photoIndexMode ? 'photo-index-active' : ''}`}
                  data-marker-number={marker.number}
                  data-marker-index={marker.id}
                  style={{ cursor: photoIndexMode ? 'move' : 'pointer', pointerEvents: 'auto' }}
                  onClick={(e) => { e.stopPropagation(); handleMarkerClick(marker, e); }}
                  onMouseDown={(e) => {
                    const hasModifier = e.shiftKey || e.ctrlKey || e.metaKey;
                    if (photoIndexMode && !hasModifier && e.button === 0) {
                      // Stop the mousedown from reaching the canvas so it doesn't
                      // start a canvas drag while the user is clicking a marker.
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      let hasMoved = false;
                      const onMove = (mv: MouseEvent) => {
                        if (!hasMoved && (Math.abs(mv.clientX - startX) > 3 || Math.abs(mv.clientY - startY) > 3)) {
                          hasMoved = true;
                          handleMarkerDragStart?.(marker.id, e);
                        }
                      };
                      const onUp = () => {
                        document.removeEventListener('mousemove', onMove);
                        document.removeEventListener('mouseup', onUp);
                      };
                      document.addEventListener('mousemove', onMove);
                      document.addEventListener('mouseup', onUp);
                    }
                  }}
                  onContextMenu={(e) => { if (photoIndexMode) handleMarkerRightClick?.(e); }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMarkerClick(marker, e as unknown as React.MouseEvent);
                  }}
                >
                  {!photoIndexMode && marker.photos.length === 0 && (
                    <title>No photos available at this location yet</title>
                  )}
                  <rect
                    x={marker.x - marker.width / 2}
                    y={marker.y - marker.height / 2}
                    width={marker.width}
                    height={marker.height}
                    fill={
                      isSelected
                        ? '#4477ff'
                        : marker.photos.some((p) => p.scan_type === 'Finished')
                          ? '#4477ff'
                          : 'red'
                    }
                    stroke={isSelected ? '#1e40af' : 'black'}
                    strokeWidth={isSelected ? 2 : 1}
                    rx="5"
                    ry="5"
                  />
                  <text
                    x={marker.x}
                    y={marker.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize={marker.fontSize}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {marker.number}
                  </text>
                  {photoIndexMode && (
                    <g
                      style={{ opacity: 0 }}
                      onMouseEnter={(e) => { (e.currentTarget as SVGGElement).style.opacity = '1'; }}
                      onMouseLeave={(e) => { (e.currentTarget as SVGGElement).style.opacity = '0'; }}
                    >
                      <circle
                        cx={marker.x + marker.width / 2}
                        cy={marker.y - marker.height / 2}
                        r="8"
                        fill="red"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); deletePhotoMarker(marker.id); }}
                      />
                      <text
                        x={marker.x + marker.width / 2}
                        y={marker.y - marker.height / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize="10"
                        fontWeight="bold"
                        style={{ pointerEvents: 'none' }}
                      >
                        ×
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      )}
    </div>
  );
};
